import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "assets/logo/logo.png";
import useReadingProgress from "Hooks/useReadingProgress";
import { auth } from "firebase.js";
import { signOut } from "firebase/auth";
import { useAuth } from "context/AuthProvider";
import LocationDropdown from "Components/Navbar/LocationDropdown";
import LocationLinks from "Components/Navbar/LocationLinks";

const Navbar = () => {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState("#home");
  const completion = useReadingProgress();
  const [navbarBg, setNavbarBg] = useState("transparent");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  useEffect(() => {
    const handleScroll = () => {
      setNavbarBg(window.pageYOffset > 120 ? "solid" : "transparent");
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navOptions = (closeMobileMenu = () => {}) => (
    <>
      <li>
        <a
          href="#home"
          className={activeNav === "#home" ? "active_link" : "text-white"}
          onClick={() => {
            setActiveNav("#home");
            closeMobileMenu();
          }}
        >
          Accueil
        </a>
      </li>
      <li>
        <a
          href="#aqbm"
          className={activeNav === "#aqbm" ? "active_link" : "text-white"}
          onClick={() => {
            setActiveNav("#aqbm");
            closeMobileMenu();
          }}
        >
          Tournois Provinciaux
        </a>
      </li>
      <li>
        <a
          href="#hebdos"
          className={activeNav === "#hebdos" ? "active_link" : "text-white"}
          onClick={() => {
            setActiveNav("#hebdos");
            closeMobileMenu();
          }}
        >
          Où jouer
        </a>
      </li>
      <li>
        <a
          href="#aboutus"
          className={activeNav === "#aboutus" ? "active_link" : "text-white"}
          onClick={() => {
            setActiveNav("#aboutus");
            closeMobileMenu();
          }}
        >
          À propos
        </a>
      </li>
      {user?.master && (
        <li>
          <Link to="/admin/dashboard" onClick={closeMobileMenu} className="text-white">
            Admin
          </Link>
        </li>
      )}
      <div className="hidden md:block">{user && <LocationDropdown />}</div>
      <div className="md:hidden w-full">{user && <LocationLinks />}</div>
    </>
  );

  return (
    <>
      <div
        className={`navbar top-0 transition-all ease-out duration-300 text-white fixed z-50 py-3 md:px-8 w-full ${
          navbarBg !== "transparent"
            ? "navbar_bg"
            : "lg:py-4 py-5 bg-gray-600 lg:bg-opacity-0 lg:h-32"
        }`}
      >
        <div className="navbar-start">
          {/* Burger for mobile */}
          <button
            className="btn btn-ghost md:hidden bg-red-500 hover:bg-red-600"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h8m-8 6h16"
              />
            </svg>
          </button>

          <Link to="/" className="cursor-pointer hidden md:block">
            {navbarBg !== "transparent" ? (
              <img className="w-28" src={logo} alt="logo" />
            ) : (
              <img className="lg:w-64" src={logo} alt="logo" />
            )}
          </Link>
        </div>

        <div className="navbar-center lg:flex">
          <ul className="menu menu-horizontal font-semibold px-4 hidden md:flex text-white">
            {navOptions()}
          </ul>
          <img className="w-24 pl-2 md:hidden" src={logo} alt="logo" />
        </div>

        <div className="navbar-end flex-col md:flex-row items-end md:items-center">
          {user ? (
            <>
              <button
                onClick={handleLogout}
                className="px-2 py-1 md:px-3 md:py-2 text-sm lg:text-md font-medium bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-xl transition"
              >
                Déconnexion
              </button>
              <span className="hidden lg:block px-4 text-white font-semibold text-lg truncate max-w-[400px]">
                {user.name}
              </span>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="px-2 py-1 md:px-5 md:py-2 mb-1 md:mb-0 md:mx-2 text-sm lg:text-md font-semibold bg-green-700 hover:bg-green-600 text-white rounded-lg shadow-xl transition"
              >
                Connexion
              </Link>
              <Link
                to="/signup"
                className="px-2 py-1 md:px-5 md:py-2 mb-1 md:mb-0 md:mx-2 text-sm lg:text-md font-semibold bg-blue-700 hover:bg-blue-600 text-white rounded-lg shadow-xl transition"
              >
                Créer un compte
              </Link>
            </>
          )}
        </div>

        <span
          className="absolute bg-gradient-to-r from-red-500 via-yellow-500 to-transparent h-1 w-full bottom-0"
          style={{ transform: `translateX(${completion - 101}%)` }}
        ></span>
      </div>

      {/* Slide-In Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 w-2/3 bg-sky-900 p-6 flex flex-col animate-slide-in-left">
          <div className="flex flex-row items-center justify-between">
            <img className="w-20 md:hidden" src={logo} alt="logo" />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="self-end text-3xl text-white hover:text-red-400"
            >
              ✕
            </button>
          </div>

          <ul className="flex flex-col gap-6 mt-6 text-lg font-semibold">
            {navOptions(() => setIsMobileMenuOpen(false))}
          </ul>
        </div>
      )}
    </>
  );
};

export default Navbar;
